import { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useCalculatorStore } from '../store/calculatorStore';
import type { ChatWorkerResponse, MultiServiceEntry } from '../api/types';
import { submitChatQuery, submitChatAdjust } from '../api/calculatorApi';
import { CombinedEstimatePanel } from '../components/chat/CombinedEstimatePanel';
import { ClarifyingBubble } from '../components/chat/ClarifyingBubble';
import { Toast } from '../components/shared/Toast';
import { ChatShareButton } from '../components/chat/ChatShareButton';
import { useToast } from '../hooks/useToast';
import { messageHasCost, decodeEstimate } from '../utils/shareEstimate';

const SERVICE_ROUTES: Record<string, string> = {
  blob_storage: '/blob-storage',
  logic_apps: '/logic-apps',
  functions: '/functions',
  app_service: '/app-service',
  bandwidth: '/bandwidth',
  log_analytics: '/log-analytics',
  sql_database: '/sql-database',
  firewall: '/firewall',
  networking: '/networking',
  data_factory: '/data-factory',
  virtual_desktop: '/virtual-desktop',
  front_door: '/front-door',
  managed_disks: '/managed-disks',
  key_vault: '/key-vault',
  azure_devops: '/azure-devops',
  site_recovery: '/site-recovery',
  aks: '/aks',
  sql_managed_instance: '/sql-managed-instance',
  application_gateway: '/application-gateway',
  vpn_gateway: '/vpn-gateway',
  bastion: '/bastion',
  azure_backup: '/azure-backup',
  azure_openai: '/azure-openai',
};

const SUGGESTION_CHIPS = [
  "How much for 50 GB/day of Log Analytics?",
  "Compare App Service P1v4 vs 2 VMs for a web app",
  "What's the cheapest way to store 10TB of archive data?",
  "Estimate a standard AVD deployment for 20 users",
];

interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface ParsedState {
  services: string[];
  [key: string]: unknown;
}

export default function AIAssistantPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applyParsedQuery = useCalculatorStore(s => s.applyParsedQuery);
  const addAdjustment = useCalculatorStore(s => s.addAdjustment);
  const { toast, showToast } = useToast();

  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);

  // Multi-service state
  const [multiServices, setMultiServices] = useState<MultiServiceEntry[] | null>(null);
  const [totalGbp, setTotalGbp] = useState<number>(0);

  // Single-service last result
  const [singleResult, setSingleResult] = useState<ChatWorkerResponse | null>(null);

  // Clarification state
  const [clarifyingQuestion, setClarifyingQuestion] = useState<string | null>(null);
  const [clarifyingOptions, setClarifyingOptions] = useState<string[]>([]);

  // Previous parsed state for adjustment turns
  const previousStateRef = useRef<ParsedState | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);

  const hasResult = multiServices !== null || singleResult !== null;
  const hasMessages = chatMessages.length > 0;

  const scrollToBottom = useCallback(() => {
    if (shouldAutoScrollRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (hasMessages) {
      scrollToBottom();
    }
  }, [chatMessages, scrollToBottom, hasMessages]);

  // Restore shared estimate from URL on mount
  useEffect(() => {
    const encoded = searchParams.get('estimate');
    if (encoded && chatMessages.length === 0) {
      const payload = decodeEstimate(encoded);
      if (payload) {
        setChatMessages([
          {
            id: Date.now().toString(),
            type: 'user',
            content: payload.q,
            timestamp: new Date(payload.t),
          },
          {
            id: (Date.now() + 1).toString(),
            type: 'ai',
            content: `${payload.r}\n\n*Shared estimate — prices may have updated since this was generated.*`,
            timestamp: new Date(payload.t),
          },
        ]);
      }
    }
  }, []);

  const handleConversationScroll = () => {
    if (!conversationRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = conversationRef.current;
    shouldAutoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50;
  };


  function applyResult(result: ChatWorkerResponse, userMessage: string) {
    // Add user message to chat
    setChatMessages(prev => [...prev, {
      id: Date.now().toString(),
      type: 'user',
      content: userMessage,
      timestamp: new Date(),
    }]);

    // KQL query response
    if ((result as any).kql_query) {
      const kqlContent = (result as any)._raw || (result as any).kql_query;
      const aiMessage = (result as any).message || "Here's a KQL query for your use case. Rates are included as comments.";

      // Format as markdown code block for proper rendering
      const formattedContent = `${aiMessage}\n\n\`\`\`kusto\n${kqlContent}\n\`\`\``;

      setChatMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'ai',
        content: formattedContent,
        timestamp: new Date(),
      }]);

      setClarifyingQuestion(null);
      setClarifyingOptions([]);
      setSingleResult(result);
      setMultiServices(null);
      return;
    }

    // Clarification
    if (result.clarifying_question) {
      setClarifyingQuestion(result.clarifying_question);
      setClarifyingOptions(result.clarifying_options ?? []);
    } else {
      setClarifyingQuestion(null);
      setClarifyingOptions([]);
    }

    // Store raw snake_case state for adjustment turns
    if (result._state) {
      previousStateRef.current = result._state as ParsedState;
    }

    // Multi-service result
    if (result.services && result.services.length >= 2) {
      setMultiServices(result.services);
      setTotalGbp(result.total_gbp ?? 0);
      setSingleResult(null);

      // Still populate Zustand store from first service's parsed_query
      const firstPq = result.services[0]?.parsed_query;
      if (firstPq) applyParsedQuery(firstPq);

      if (hasResult) {
        addAdjustment(userMessage.slice(0, 40) + (userMessage.length > 40 ? '…' : ''));
      }
      return;
    }

    // Single-service result
    if (result.parsed_query) {
      applyParsedQuery(result.parsed_query);
      setSingleResult(result);
      setMultiServices(null);

      const services = result.parsed_query.services ?? [];
      const firstService = services[0];
      if (firstService && SERVICE_ROUTES[firstService] && !hasResult) {
        navigate(SERVICE_ROUTES[firstService]);
      } else if (hasResult) {
        addAdjustment(userMessage.slice(0, 40) + (userMessage.length > 40 ? '…' : ''));
      }
      return;
    }

    // Fallback: if we have a message but no parsed data, display it as an AI message
    if (result.message && !result.clarifying_question) {
      setChatMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'ai' as const,
        content: result.message ?? 'No response',
        timestamp: new Date(),
      }]);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const query = input.trim();
    if (!query || isProcessing) return;

    setError(null);
    const isAdjustment = hasResult && previousStateRef.current !== null;

    if (isAdjustment) {
      setIsUpdating(true);
    } else {
      setIsProcessing(true);
    }

    try {
      let data: ChatWorkerResponse;
      if (isAdjustment && previousStateRef.current) {
        data = await submitChatAdjust(query, previousStateRef.current as Record<string, unknown>);
      } else {
        data = await submitChatQuery(query);
      }

      if (data.error) {
        const errorMsg = data.message ?? data.error ?? 'An error occurred';
        setError(errorMsg);
        setChatMessages(prev => [...prev,
          {
            id: Date.now().toString(),
            type: 'user',
            content: query,
            timestamp: new Date(),
          },
          {
            id: (Date.now() + 1).toString(),
            type: 'ai' as const,
            content: `⚠️ ${errorMsg}`,
            timestamp: new Date(),
          }
        ]);
      } else {
        applyResult(data, query);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(msg);
      setChatMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'user',
        content: query,
        timestamp: new Date(),
      }]);
    } finally {
      setIsProcessing(false);
      setIsUpdating(false);
      setInput('');
    }
  }

  async function handleClarifySelect(option: string) {
    setInput(option);
    setClarifyingQuestion(null);
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: 'var(--background)' }}>
      <Helmet>
        <title>AI Assistant | AzureCalc.uk</title>
        <meta
          name="description"
          content="Describe your Azure workload in plain English and let AzureCalc.uk map it to calculator-ready inputs for UK South pricing estimates."
        />
        <link rel="canonical" href="https://www.azure-calc.co.uk" />
      </Helmet>


      {/* Conversation area */}
      <div
        ref={conversationRef}
        onScroll={handleConversationScroll}
        className="flex-1 overflow-y-auto custom-scrollbar px-6 py-8"
      >
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Greeting message (only on first load) */}
          {!hasMessages && !hasResult && (
            <div className="text-center space-y-6">
              <div className="text-4xl mb-6">👋</div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Hello! I'm the AzureCalc.uk AI assistant.
              </h1>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                Ask me anything about Azure pricing for UK South — I'll give you GBP estimates,
                explain billing traps, and link you to the right calculator.
              </p>
              <p className="text-sm font-semibold mt-8" style={{ color: 'var(--text-tertiary)' }}>
                Try asking:
              </p>
              <div className="flex flex-col gap-2 mt-4">
                {SUGGESTION_CHIPS.map(chip => (
                  <button
                    key={chip}
                    onClick={() => setInput(chip)}
                    className="text-left px-4 py-3 rounded-sm transition-all"
                    style={{
                      border: '1px solid var(--outline-variant)',
                      backgroundColor: 'var(--surface-container-high)',
                      color: 'var(--text-secondary)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--surface-container-highest)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--surface-container-high)'}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chat messages */}
          {chatMessages.map(msg => (
            <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.type === 'ai' && (
                <div className="flex gap-3 max-w-xl">
                  <div
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: '#0078d4' }}
                  >
                    AC
                  </div>
                  <div
                    className="rounded-lg px-4 py-3"
                    style={{
                      backgroundColor: 'var(--surface-container-high)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" className="text-[#0078d4] hover:underline" />
                        ),
                        code: ({ inline, className, children }: any) => {
                          if (inline) {
                            return <code className="bg-gray-200 px-1 py-0.5 rounded text-sm">{children}</code>;
                          }

                          const match = (className || '').match(/language-(\w+)/);
                          const language = match ? match[1].toLowerCase() : '';
                          const codeText = String(children).replace(/\n$/, '');

                          // Special handling for KQL/Kusto code blocks
                          if (language === 'kusto' || language === 'kql') {
                            return (
                              <div className="my-4 rounded-lg overflow-hidden border" style={{ borderColor: 'var(--outline-variant)' }}>
                                {/* Code block with dark background */}
                                <div
                                  className="p-4 overflow-x-auto font-mono text-sm"
                                  style={{
                                    backgroundColor: '#1e1e1e',
                                    color: '#d4d4d4',
                                  }}
                                >
                                  <pre style={{ margin: 0 }}>
                                    <code>{codeText}</code>
                                  </pre>
                                </div>

                                {/* Action buttons */}
                                <div
                                  className="flex gap-2 p-3 border-t"
                                  style={{
                                    borderColor: 'var(--outline-variant)',
                                    backgroundColor: 'var(--surface-container-high)',
                                  }}
                                >
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(codeText);
                                    }}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-all"
                                    style={{
                                      backgroundColor: '#0078d4',
                                      color: 'white',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                                  >
                                    <span className="material-symbols-outlined text-[16px]">content_copy</span>
                                    <span>Copy</span>
                                  </button>

                                  <a
                                    href="https://portal.azure.com/#view/Microsoft_Azure_Monitoring/Logs.ReactView"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-all"
                                    style={{
                                      color: '#0078d4',
                                      border: '1px solid #0078d4',
                                    }}
                                    title="Paste this query into Azure Portal and run it in your Log Analytics workspace"
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0, 120, 212, 0.1)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                  >
                                    <span className="material-symbols-outlined text-[16px]">open_in_new</span>
                                    <span>Azure Portal</span>
                                  </a>
                                </div>
                              </div>
                            );
                          }

                          // Standard code block for other languages
                          return (
                            <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-sm">
                              <code className={className}>{children}</code>
                            </pre>
                          );
                        },
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                    <p className="text-[10px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
                      {msg.timestamp.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {messageHasCost(msg.content) && (
                      <div className="mt-3">
                        <ChatShareButton
                          userMessage={chatMessages[chatMessages.findIndex(m => m.id === msg.id) - 1]?.content || ''}
                          aiResponse={msg.content}
                          onShared={() => showToast('Link copied to clipboard!')}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
              {msg.type === 'user' && (
                <div className="max-w-xl">
                  <div
                    className="rounded-lg px-4 py-3"
                    style={{
                      backgroundColor: '#0078d4',
                      color: 'white',
                    }}
                  >
                    <p>{msg.content}</p>
                    <p className="text-[10px] mt-2 opacity-70">
                      {msg.timestamp.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {(isProcessing || isUpdating) && (
            <div className="flex justify-start">
              <div className="flex gap-3 max-w-xl">
                <div
                  className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: '#0078d4' }}
                >
                  AC
                </div>
                <div
                  className="rounded-lg px-4 py-3 flex gap-1"
                  style={{ backgroundColor: 'var(--surface-container-high)' }}
                >
                  <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-tertiary)', animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-tertiary)', animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-tertiary)', animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          {/* Clarification bubble */}
          {clarifyingQuestion && (
            <ClarifyingBubble
              question={clarifyingQuestion}
              options={clarifyingOptions}
              onSelect={handleClarifySelect}
            />
          )}

          {/* Multi-service combined estimate */}
          {multiServices && (
            <CombinedEstimatePanel
              services={multiServices}
              totalGbp={totalGbp}
              isUpdating={isUpdating}
            />
          )}

          {/* Single-service last result */}
          {singleResult?.calculation && !multiServices && (
            <div
              className="rounded-lg p-4"
              style={{
                border: '1px solid var(--outline-variant)',
                backgroundColor: 'var(--surface-container-high)',
              }}
            >
              <p className="text-[11px] font-mono uppercase tracking-wider mb-1" style={{ color: 'var(--text-tertiary)' }}>
                Last estimate
              </p>
              <p className="text-2xl font-bold font-mono" style={{ color: '#0078d4' }}>
                £{((singleResult.calculation as { total_monthly_estimate?: number; monthly_estimate?: number }).total_monthly_estimate ?? (singleResult.calculation as { monthly_estimate?: number }).monthly_estimate ?? 0).toFixed(2)}
                <span className="text-sm font-normal" style={{ color: 'var(--text-tertiary)' }}>/mo</span>
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-xs p-3 rounded-lg" style={{ color: 'var(--color-error, #a4262c)', backgroundColor: 'var(--surface-container)' }}>
              <span className="material-symbols-outlined text-sm shrink-0">error</span>
              <span>{error}</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input bar (fixed at bottom) */}
      <div
        className="shrink-0 border-t px-6 py-4"
        style={{
          borderColor: 'var(--outline-variant)',
          backgroundColor: 'var(--surface-container)',
        }}
      >
        <div className="max-w-2xl mx-auto space-y-3">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isProcessing || isUpdating}
              placeholder={hasMessages ? "Adjust your estimate — e.g. 'make it 80 GB/day'..." : "Ask about Azure pricing..."}
              className="flex-1 rounded-sm py-2.5 px-3 text-sm resize-none focus:outline-none transition-all"
              rows={3}
              style={{
                backgroundColor: 'var(--surface-container-highest)',
                border: '1px solid var(--outline-variant)',
                color: 'var(--text-primary)',
                opacity: isProcessing || isUpdating ? 0.5 : 1,
              }}
            />
            <button
              type="submit"
              disabled={isProcessing || isUpdating || !input.trim()}
              className="shrink-0 w-10 h-10 flex items-center justify-center rounded-sm transition-all disabled:opacity-30"
              style={{ backgroundColor: '#0078d4', color: 'white' }}
            >
              {isProcessing || isUpdating ? (
                <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
              ) : (
                <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>send</span>
              )}
            </button>
          </form>
          <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
            20 messages per minute · Prices updated nightly · GBP
          </p>
        </div>
      </div>

      {/* Toast notification */}
      <Toast message={toast.message} visible={toast.visible} />
    </div>
  );
}
